/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

// Add third-party dependencies.
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { useState } from 'react';

// Add local dependencies.
import type { ChangelogEntry as ChangelogEntryType } from '../../../types/changelog';
import {
  formatChangelogDate,
  groupChangesByType,
  getChangeTypeOrder,
  CHANGE_TYPE_CONFIG,
} from '../../../utils/changelogUtils';
import ChangelogChange from './ChangelogChange';

/**
 * Props for the ChangelogEntry component.
 */
interface ChangelogEntryProps {
  /** The changelog entry data to display */
  entry: ChangelogEntryType;
  /** Whether this is the latest entry */
  isLatest?: boolean;
}

/**
 * Component for displaying a complete changelog entry for a specific version.
 * Shows version info, changes grouped by type, and handles collapsible content.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The changelog entry component
 *
 * @example
 * return (
 *   <ChangelogEntry
 *     entry={{
 *       version: '1.0.0',
 *       date: '2025-01-04T12:00:00.000Z',
 *       title: 'Major Release',
 *       changes: [...]
 *     }}
 *     isLatest={true}
 *   />
 * );
 */
const ChangelogEntry: React.FC<ChangelogEntryProps> = ({
  entry,
  isLatest = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(isLatest);

  const formattedDate = formatChangelogDate(entry.date);
  const groupedChanges = groupChangesByType(entry.changes);
  const changeTypeOrder = getChangeTypeOrder();

  // Filter to only show change types that have changes
  const availableTypes = changeTypeOrder.filter(
    type => groupedChanges[type]?.length > 0
  );

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Card
      sx={{
        mb: 3,
        border: isLatest ? 2 : 1,
        borderColor: isLatest ? 'primary.main' : 'divider',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      {/* Header */}
      <CardContent
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={handleToggleExpanded}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                v{entry.version}
              </Typography>
              {isLatest && (
                <Chip
                  label="Latest"
                  size="small"
                  color="primary"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Box>

            {entry.title && (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
                {entry.title}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary">
              Released {formattedDate}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              {entry.changes.length} change
              {entry.changes.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" color="primary">
              {isExpanded ? 'Click to collapse' : 'Click to expand'}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Expandable content */}
      {isExpanded && (
        <>
          <Divider />
          <CardContent>
            {/* Description */}
            {entry.description && (
              <Typography
                variant="body1"
                sx={{ mb: 3, fontStyle: 'italic', color: 'text.secondary' }}
              >
                {entry.description}
              </Typography>
            )}

            {/* Changes by type */}
            {availableTypes.map((type, index) => {
              const changes = groupedChanges[type];
              const config = CHANGE_TYPE_CONFIG[type];

              if (!changes || changes.length === 0) return null;

              return (
                <Box
                  key={type}
                  sx={{ mb: index === availableTypes.length - 1 ? 0 : 3 }}
                >
                  {/* Type header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: `${config.color}.main`,
                      }}
                    >
                      {config.label}
                    </Typography>
                    <Chip
                      label={changes.length}
                      size="small"
                      color={config.color}
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', minWidth: 32 }}
                    />
                  </Box>

                  {/* Changes list */}
                  <Box sx={{ pl: 2 }}>
                    {changes.map(change => (
                      <ChangelogChange
                        key={`${type}-${change.message.slice(0, 50).replace(/[^a-zA-Z0-9]/g, '-')}`}
                        change={change}
                        showType={false} // Don't show type since we're already grouped
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}

            {/* No changes message */}
            {availableTypes.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                No changes recorded for this version.
              </Typography>
            )}
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default ChangelogEntry;
